import WidgetDeviceType from '@/enums/WidgetDeviceType';
import PaginationMethod from '@/enums/PaginationMethod';
import ReviewPopupStyle from '@/enums/ReviewPopupStyle';
import WidgetType from '@/enums/WidgetType';
import WidgetStyle from '@/enums/WidgetStyle';
import ThumbnailDisplayType from '@/enums/ThumbnailDisplayType';
import { pathWithParams } from '@/lib/params';
import { POPUP_PER } from '@/constants/PARAMS';
import i18n from '@/lib/i18n';

const state = {
  widget: null,
  cremaDomainPrefix: '',
  hasMinimumItems: null,
  isMobile: true,
  iframeId: null,
  brandName: null,
  reviewId: null,
  deviceViewport: 'mobile'
};

const mutations = {
  STORE_RESPONSE_DATA(state, body) {
    Object.assign(state, body);
  },
  SET_IS_MOBILE(state, isMobile) {
    state.isMobile = isMobile;
  },
  SET_IFRAME_ID(state, iframeId) {
    state.iframeId = iframeId || null;
  },
  SET_REVIEW_ID(state, reviewId) {
    state.reviewId = reviewId;
  },
  SET_HAS_MINIMUM_ITEMS(state, hasMinimumItems) {
    if (state.hasMinimumItems === null) state.hasMinimumItems = hasMinimumItems;
  },
  SET_DEVICE_VIEWPORT(state, deviceViewport) {
    state.deviceViewport = deviceViewport;
  }
};

// TODO: 리뷰 상세 팝업이 vue로 포팅되면 popupPageParams를 제거하고 vue 포팅된 팝업의 per를 항상 7개로 설정하자
function popupPageParams(page, per, index) {
  // page * 1: 저장 위치에 따라 page값이 문자열인 경우도 있으므로 항상 숫자로 변환해 오류를 방지하자
  const totalIndex = (page * 1 - 1) * per + index + 1;
  const newPage = Math.ceil(totalIndex / POPUP_PER);
  return {
    page: newPage,
    per: POPUP_PER
  };
}

const getters = {
  isMobileViewport({ deviceViewport }) {
    return deviceViewport === 'mobile';
  },
  isPcViewport({ deviceViewport }) {
    return deviceViewport === 'pc';
  },
  isFullscreenPopup({ iframeId }) {
    return (
      iframeId !== null && iframeId.indexOf('crema-fullscreen-popup') !== -1
    );
  },
  enablePaginationScroll({ widget }) {
    return widget.pagination_method === PaginationMethod.PAGINATE
      ? widget.enable_pagination_scroll
      : false;
  },
  sessionedPathWithParams: (
    _state,
    _getters,
    { session: { sessionParams } }
  ) => (path, params) => {
    return pathWithParams(path, {
      ...params,
      ...sessionParams
    });
  },
  allMediaReviewsPopupUrl(
    { widget: { widget_device_type, id, media_summary_sort, widget_type } },
    getters,
    { brandCode, productCode, reviewSettings: { reviewSettings } }
  ) {
    const pathV1 =
      widget_device_type === WidgetDeviceType.MOBILE
        ? `/${brandCode}/mobile/products/media`
        : `/${brandCode}/products/media`;
    const pathV2 =
      widget_device_type === WidgetDeviceType.MOBILE
        ? `${window.location.origin}/v2/${brandCode}/mobile/product_reviews/media_collage`
        : `${window.location.origin}/v2/${brandCode}/product_reviews/media_collage`;
    const path = reviewSettings.enable_vue_product_reviews_media_collage_popup
      ? pathV2
      : pathV1;
    return getters.sessionedPathWithParams(path, {
      media_summary: true,
      parent_widget_id: id,
      parent_widget_type: widget_type,
      product_code: productCode,
      sort: media_summary_sort,
      iframe: true
    });
  },
  mediaReviewPopupUrlByReviewPopupStyle: (
    {
      widget: {
        id,
        widget_device_type,
        widget_type,
        mobile_review_popup_style,
        pc_review_popup_style,
        media_summary_sort
      }
    },
    getters
  ) => (reviewId, mediaIndex, reviewIndex) => {
    const isMobileWidget = widget_device_type === WidgetDeviceType.MOBILE;
    if (isMobileWidget && mobile_review_popup_style === ReviewPopupStyle.SHOW)
      return getters.showReviewPopupUrl(reviewId, mediaIndex, reviewIndex, {
        media_summary: true,
        parent_widget_id: id,
        parent_widget_type: widget_type,
        review_id: reviewId,
        sort: media_summary_sort,
        with_media: true
      });
    if (
      (isMobileWidget ? mobile_review_popup_style : pc_review_popup_style) ===
      ReviewPopupStyle.SHORTS
    )
      return getters.shortsReviewPopupUrl(reviewId, mediaIndex, reviewIndex, {
        sort: media_summary_sort
      });
    else return getters.mediaReviewPopupUrl(reviewId, mediaIndex, reviewIndex);
  },
  mediaReviewPopupUrl: (
    { widget: { id, media_summary_sort, widget_type } },
    getters,
    { brandCode, productCode, reviewSettings: { reviewSettings } }
  ) => (reviewId, mediaIndex, reviewIndex) => {
    const path = reviewSettings.enable_vue_product_reviews_media_collage_popup
      ? `${window.location.origin}/v2/${brandCode}/product_reviews/media_collage`
      : `/${brandCode}/products/media`;
    return getters.sessionedPathWithParams(path, {
      media_index: mediaIndex,
      media_summary: true,
      parent_widget_id: id,
      parent_widget_type: widget_type,
      product_code: productCode,
      review_id: reviewId,
      sort: media_summary_sort,
      iframe: true,
      page: Math.ceil(reviewIndex + 1 / POPUP_PER)
    });
  },
  shortsReviewPopupUrl: (
    { widget: { id, parent_widget_id, posts_per_page, widget_type } },
    getters,
    { brandCode, productCode, categoryCode }
  ) => (reviewId, mediaIndex, reviewIndex, popupParams, curProductCode) => {
    const { page, per, ...popupFilterParams } = popupParams;
    return getters.sessionedPathWithParams(
      `${window.location.origin}/v2/${brandCode}/reviews/${reviewId}/shorts`,
      {
        media_index: mediaIndex,
        parent_widget_id: parent_widget_id || id,
        parent_widget_type: widget_type,
        category_code: categoryCode,
        product_code: curProductCode || productCode,
        ...popupFilterParams,
        ...popupPageParams(page || 1, per || posts_per_page, reviewIndex)
      }
    );
  },
  shortsReviewPopupUrlForShare: (
    { widget: { id, parent_widget_id, posts_per_page } },
    _getters,
    { brandCode, productCode, categoryCode, parentWidgetType }
  ) => (reviewId, mediaIndex, reviewIndex, popupParams, curProductCode) => {
    const { page, per, ...popupFilterParams } = popupParams;
    return pathWithParams(
      `${window.location.origin}/v2/${brandCode}/reviews/${reviewId}/shorts`,
      {
        media_index: mediaIndex,
        parent_widget_id: parent_widget_id || id,
        parent_widget_type: parentWidgetType,
        category_code: categoryCode,
        product_code: curProductCode || productCode,
        ...popupFilterParams,
        ...popupPageParams(page || 1, per || posts_per_page, reviewIndex)
      }
    );
  },
  showReviewPopupUrl: (
    { widget: { widget_device_type, parent_widget_id, id, posts_per_page } },
    getters,
    { brandCode, productCode, categoryCode, reviewSettings: { reviewSettings } }
  ) => (reviewId, mediaIndex, reviewIndex, popupParams, curProductCode) => {
    const isMobileWidget = getters.isManagingReviews
      ? getters.isMobileViewport
      : widget_device_type === WidgetDeviceType.MOBILE;
    const pathV1 = isMobileWidget
      ? `/${brandCode}/mobile/reviews/${reviewId}/show_popup`
      : `/${brandCode}/reviews/${reviewId}/photo_review_popup`;
    const pathV2 = isMobileWidget
      ? `/v2/${brandCode}/mobile/reviews/${reviewId}`
      : `/v2/${brandCode}/reviews/${reviewId}`;
    const path = reviewSettings.enable_vue_reviews_show ? pathV2 : pathV1;
    const { page, per, ...popupFilterParams } = popupParams;
    return getters.sessionedPathWithParams(path, {
      media_index: mediaIndex,
      parent_widget_id: parent_widget_id || id,
      category_code: categoryCode,
      product_code: curProductCode || productCode,
      ...popupFilterParams,
      ...popupPageParams(page || 1, per || posts_per_page, reviewIndex)
    });
  },
  reviewPopupUrl: (
    {
      widget: {
        widget_device_type,
        mobile_review_popup_style,
        pc_review_popup_style
      }
    },
    getters
  ) => (review, mediaIndex, reviewIndex, popupParams, curProductCode) => {
    const isMobileWidget = getters.isManagingReviews
      ? getters.isMobileViewport
      : widget_device_type === WidgetDeviceType.MOBILE;
    if (
      (isMobileWidget ? mobile_review_popup_style : pc_review_popup_style) ===
        ReviewPopupStyle.SHORTS &&
      review.media_count > 0
    )
      return getters.shortsReviewPopupUrl(
        review.id,
        mediaIndex,
        reviewIndex,
        popupParams,
        curProductCode
      );
    else
      return getters.showReviewPopupUrl(
        review.id,
        mediaIndex,
        reviewIndex,
        popupParams,
        curProductCode
      );
  },
  noItemActionRequired({ widget: { no_item_action_type }, hasMinimumItems }) {
    return !!no_item_action_type && !hasMinimumItems;
  },
  device({ isMobile }) {
    return isMobile ? 'mobile' : 'pc';
  },
  headerSectionShowable(
    {
      widget: {
        show_header_section,
        show_title,
        show_search_section,
        show_score_filter,
        show_top_filter_menu,
        category_id
      }
    },
    getters
  ) {
    return (
      show_header_section &&
      (show_title ||
        show_search_section ||
        show_score_filter ||
        (!category_id && show_top_filter_menu) ||
        getters.showToggleFilterSection)
    );
  },
  // AppHeader(V1)를 사용하는 위젯 전용. 리스트(V3)/게시판 계열은 appHeaderV2AndTitleShowable 사용
  appHeaderShowable({ widget }, getters) {
    return getters.headerSectionShowable && widget.show_title;
  },
  // AppHeaderV2(AppTitleSection)를 사용하는 위젯 전용. 그 외 위젯은 appHeaderShowable 사용
  appHeaderV2AndTitleShowable({ widget }, getters) {
    return (
      widget.show_header_section &&
      widget.show_title &&
      (widget.use_title_and_description || getters.reviewBoardLinkShowable)
    );
  },
  reviewBoardLinkShowable({ widget }, _getters, { reviewSettings }) {
    return !!(
      widget.use_reviews_button &&
      reviewSettings.reviewSettings.reviews_index_url
    );
  },
  displayMediaReviewsFirstToggle({ widget }) {
    return (
      widget.widget_type === WidgetType.PRODUCT_REVIEWS &&
      !widget.is_photo_only &&
      !widget.review_show_photo_first
    );
  },
  displayMediaReviewsFirstToggleListV3({ widget }) {
    return (
      !widget.is_photo_only &&
      !widget.review_show_photo_first &&
      widget.show_filter_section &&
      widget.show_review_photo_first_button
    );
  },
  showCategoryFilter({ widget }) {
    return !!(
      widget.widget_type === WidgetType.REVIEWS &&
      !widget.category_id &&
      widget.show_top_filter_menu
    );
  },
  showOptionTypeFilter({ widget }) {
    return widget.widget_type === WidgetType.PRODUCT_REVIEWS;
  },
  showScoreFilter({ widget }, _getters, { reviewSettings }) {
    return (
      reviewSettings.reviewSettings.show_score &&
      widget.show_filter_section &&
      widget.show_score_filter
    );
  },
  showBrandLocaleFilter(
    _state,
    _getters,
    { translation: { locale } },
    rootGetters
  ) {
    return rootGetters['window/browserLanguage'] !== locale;
  },
  showToggleFilterSection(_state, getters) {
    return getters.showBrandLocaleFilter || getters.showVerifiedBuyerFilter;
  },
  showVerifiedBuyerFilter(_state, _getters, { reviewSettings }) {
    return (
      reviewSettings.reviewSettings.use_verified_buyer_badge &&
      reviewSettings.reviewSettings.use_verified_buyer_filter
    );
  },
  showFilterSection(
    { widget },
    getters,
    { reviewOptionTypes: { reviewOptionTypes } }
  ) {
    return !!(
      widget.show_header_section &&
      (getters.showCategoryFilter ||
        getters.showScoreFilter ||
        (getters.showOptionTypeFilter && reviewOptionTypes.length))
    );
  },
  nthReviewWidgetStylePopup({ widget }) {
    return !!WidgetStyle.NTH_REVIEW_STYLES.includes(widget.parent_widget_style);
  },
  isManagingReviews({ widget }) {
    return widget.widget_type === WidgetType.MANAGING_REVIEWS;
  },
  thumbnailShape({ widget }) {
    const key = ThumbnailDisplayType.key(widget.thumbnail_display_type);
    return key ? key.toLowerCase() : 'original';
  }
};

const actions = {
  fetchWidget(
    { state: { isMobile, iframeId }, rootState: { api, window }, commit },
    {
      id,
      parent_widget_id,
      widget_type,
      widget_style,
      preview,
      original_widget_id,
      parent_url,
      install_method
    }
  ) {
    return api
      .fetchWidget({
        id,
        parent_widget_id,
        widget_type,
        widget_style,
        preview,
        original_widget_id,
        parent_url,
        install_method,
        mobile: isMobile ? '1' : null
      })
      .then(({ data }) => {
        const widget = data.widget;
        if (!widget) return false;
        if (!widget.widget_displayable) window.windowService.sendHide(iframeId);

        commit('STORE_RESPONSE_DATA', {
          widget: { ...widget },
          cremaDomainPrefix: data.crema_domain_prefix,
          appBuilderType: data.app_builder_type,
          brandName: data.brand_name
        });

        return true;
      });
  },
  confirmForLogin({
    rootState: {
      window: { windowService },
      reviewSettings: { reviewSettings }
    }
  }) {
    return windowService
      .confirm(i18n.t('app.require_login_confirmation'))
      .then(response => {
        if (response) {
          const loginUrl = windowService.loginUrl(
            reviewSettings.mall_login_url,
            reviewSettings.return_url_param
          );
          windowService.redirectTo(loginUrl, { return_url: true });
        }
      });
  },
  showNotices({
    state: { isMobile },
    rootState: {
      brandCode,
      window: { windowService }
    }
  }) {
    const url = isMobile
      ? `/${brandCode}/mobile/notices`
      : `/${brandCode}/notices`;
    windowService.openFullscreenPopup(url);
  },
  openReviewPopup(
    {
      rootState: {
        window: { windowService }
      },
      getters: { reviewPopupUrl }
    },
    { review, index, productCode, popupParams, event }
  ) {
    windowService.openFullscreenPopup(
      reviewPopupUrl(review, 1, index, popupParams, productCode),
      event?.currentTarget
    );
  },
  openShowReviewPopup(
    {
      rootState: {
        window: { windowService }
      },
      getters: { showReviewPopupUrl }
    },
    { review, index, productCode, popupParams, event }
  ) {
    windowService.openFullscreenPopup(
      showReviewPopupUrl(review.id, 1, index, popupParams, productCode),
      event?.currentTarget
    );
  },
  clickMoreReviews({ rootState: { api } }, { id }) {
    api.clickMoreReviews(id);
  }
};

export default { namespaced: true, state, mutations, getters, actions };
